// savedJobs.js — saved jobs (decided by Deepak with Mayank, 2 Nov, on #85).
//
// Running a layout no longer keeps a job. A user saves a run worth keeping,
// with a name (POST /saved-jobs); saving moves their draft cartons in the run
// into their library, so the 3-day draft clean-up never takes them. The Jobs
// tray lists saved jobs — an admin's lists every one in their organisation,
// each with its owner's name — and the LAST run, saved or not, so someone who
// closed the window by mistake can still save it when they come back. Older
// unsaved runs leave the tray. A saved job is the Auto Imposer's layout and
// setup; later changes made in the Editor are not part of it.
//
// Transport-free, so the rules below are tested through their generated mirror
// (src/savedjobs.test.js); the fetchers live in savedJobsApi.js.

export const NAME_MAX = 200;

const path = (id) => `/saved-jobs/${encodeURIComponent(id)}`;

/** [path, options] for each route; bodies are what leaves the browser. */
export const SAVED_JOB_ROUTES = {
  list: () => ['/saved-jobs', {}],
  get: (id) => [path(id), {}],
  // Decided 2 Nov (b): a saved job opens its own cartons for whoever can see
  // it — a colleague's private ones for an admin, deleted ones (#88) — read-only.
  carton: (id, cartonId) => [`${path(id)}/cartons/${encodeURIComponent(cartonId)}`, {}],
  cartonSource: (id, cartonId) => [`${path(id)}/cartons/${encodeURIComponent(cartonId)}/source`, {}],
  save: ({ jobId, name, cartonIds, setup }) => ['/saved-jobs', {
    method: 'POST',
    body: { job_id: jobId, name: String(name || '').trim(), carton_ids: cartonIds || [], setup: setup || {} },
  }],
  rename: (id, name) => [path(id), { method: 'PATCH', body: { name: String(name || '').trim() } }],
  remove: (id) => [path(id), { method: 'DELETE' }],
};

/**
 * Where a reopened job's cartons come from (#88): a saved job's own routes, or
 * the Library asked to include deleted cartons (the last run). Anything else —
 * the bench put back after a refresh — reads the Library as it always has.
 */
export function cartonPaths(id, via) {
  if (via && via.savedJobId) {
    return { carton: SAVED_JOB_ROUTES.carton(via.savedJobId, id)[0],
      source: SAVED_JOB_ROUTES.cartonSource(via.savedJobId, id)[0] };
  }
  const q = via && via.includeDeleted ? '?include_deleted=true' : '';
  const base = `/library/cartons/${encodeURIComponent(id)}`;
  return { carton: `${base}${q}`, source: `${base}/source${q}` };
}

/** Why a name can't be used, or null. The server says the same with a 422. */
export function nameProblem(name) {
  const n = typeof name === 'string' ? name.trim() : '';
  if (!n) return 'Give the job a name.';
  if (n.length > NAME_MAX) return `A name can be at most ${NAME_MAX} characters.`;
  return null;
}

/**
 * What saving a run sends, from the setup recorded when it was run: the
 * library ids of its cartons (the ones a save moves into the library when they
 * are drafts), and the setup itself — quantities, per-carton settings, sheet,
 * margins — which is what opening the saved job puts back.
 */
export function saveRequestFrom(ctx) {
  const cartons = (Array.isArray(ctx?.cartons) ? ctx.cartons : [])
    .filter((c) => c && typeof c.libraryId === 'string' && c.libraryId);
  const cartonIds = [...new Set(cartons.map((c) => c.libraryId))];
  return {
    cartonIds,
    setup: { cartons, sheet: ctx?.sheet || null, margins: ctx?.margins || null },
  };
}

/**
 * The Save job control for the run on screen.
 *   'hidden'  — nothing on screen to save
 *   'save'    — a run of yours that is not saved yet
 *   'saved'   — already saved (yours, or for an admin someone else's)
 *   'blocked' — this run cannot be saved (`why` says so)
 */
export function saveControl({ result, jobId, saved, mine = true }) {
  if (!result) return { state: 'hidden' };
  if (saved) {
    return {
      state: 'saved',
      label: saved.mine === false
        ? `${saved.owner_name || 'Someone else'}’s saved job “${saved.name}”`
        : `Saved as “${saved.name}”`,
    };
  }
  if (!jobId) {
    return {
      state: 'blocked',
      why: 'This run did not go through a background job, so it can’t be saved. Run the layout again to save it.',
    };
  }
  if (!mine) return { state: 'blocked', why: 'Only the person who ran a layout can save it.' };
  return { state: 'save' };
}

const plural = (n, one, many) => (n === 1 ? one : many);
const quoted = (names) => names.map((x) => `“${x}”`).join(', ');

/**
 * What the dialog says once a job is saved: the drafts that went into the
 * library, and any of them that duplicate a library carton (decided 2 Nov:
 * saved anyway, and said so here), any carton that is gone, and any carton
 * that is someone else's draft.
 */
export function saveOutcome(resp) {
  const saved = Array.isArray(resp?.saved_to_library) ? resp.saved_to_library : [];
  const gone = Array.isArray(resp?.not_available) ? resp.not_available : [];
  const theirs = Array.isArray(resp?.left_as_draft) ? resp.left_as_draft : [];
  const lines = [];
  if (saved.length) {
    lines.push(`${saved.length} draft ${plural(saved.length, 'carton is', 'cartons are')} now in your library: ${quoted(saved.map((c) => c.name))}.`);
  }
  for (const c of saved) {
    const dups = Array.isArray(c.duplicates) ? c.duplicates : [];
    if (!dups.length) continue;
    const more = dups.length > 1 ? ` and ${dups.length - 1} more` : '';
    lines.push(`“${c.name}” has the same die and artwork as “${dups[0].name}”${more}, already in the library. Both are kept, and the Library marks them as duplicates.`);
  }
  if (gone.length) {
    lines.push(`${gone.length} ${plural(gone.length, 'carton is', 'cartons are')} no longer in the library, so ${plural(gone.length, 'it', 'they')} won’t come back with this job.`);
  }
  if (theirs.length) {
    lines.push(`${theirs.length} ${plural(theirs.length, 'carton is', 'cartons are')} someone else’s draft, so ${plural(theirs.length, 'it stays a draft', 'they stay drafts')} and may be removed after 3 days.`);
  }
  return { title: `Saved as “${resp?.name || ''}”.`, lines };
}

/** The tray's saved-jobs rows: an owner's name only on someone else's. */
export function savedJobRows(items) {
  return (Array.isArray(items) ? items : [])
    .filter((it) => it && it.id && it.job_id)
    .map((it) => ({
      id: it.id, jobId: it.job_id, poll: it.poll, name: it.name,
      owner: it.mine === false ? (it.owner_name || 'Someone else') : null,
      mine: it.mine !== false,
      createdAt: it.created_at || null,
    }));
}

/** Saved jobs by the run they keep — how the screen knows a run is saved. */
export function savedByJob(items) {
  const out = {};
  for (const it of Array.isArray(items) ? items : []) if (it && it.job_id) out[it.job_id] = it;
  return out;
}

/**
 * ui_state `lastRun`: the latest SUCCESSFUL run, with the setup it ran with,
 * kept so it can still be saved after the window is closed (decided 2 Nov).
 * Kept outside `jobContexts` on purpose: the backend's draft clean-up ignores
 * `jobContexts` (older runs do not keep their drafts) but keeps any draft
 * named anywhere else in ui_state — so this run's drafts survive until it is
 * saved or replaced by a newer run.
 */
export function lastRunEntry({ jobId, poll, ctx, at }) {
  if (!jobId) return null;
  return {
    jobId, poll: poll || null, at: Number(at) || 0,
    cartons: Array.isArray(ctx?.cartons) ? ctx.cartons : [],
    sheet: ctx?.sheet || null, margins: ctx?.margins || null,
  };
}

/** Read `lastRun` back from a hydrated ui_state; anything malformed is none. */
export function readLastRun(uiState) {
  const r = uiState && uiState.lastRun;
  if (!r || typeof r !== 'object' || typeof r.jobId !== 'string' || !r.jobId) return null;
  return {
    jobId: r.jobId, poll: typeof r.poll === 'string' ? r.poll : null, at: Number(r.at) || 0,
    cartons: Array.isArray(r.cartons) ? r.cartons : [],
    sheet: r.sheet && typeof r.sheet === 'object' ? r.sheet : null,
    margins: r.margins && typeof r.margins === 'object' ? r.margins : null,
  };
}

/** The last run, if it still waits to be saved. */
export function unsavedLastRun(lastRun, items) {
  if (!lastRun || !lastRun.jobId) return null;
  return savedByJob(items)[lastRun.jobId] ? null : lastRun;
}

/**
 * Tray rows that leave when a run finishes: every other FINISHED layout run.
 * Unsaved runs leave the tray (decided 2 Nov); the newest stays as the last
 * run, and saved ones are listed under Saved jobs instead. Running rows and
 * other kinds (a PDF export, a plan) are left alone.
 */
export function rowsLeavingTheTray(rows, keepJobId) {
  return (Array.isArray(rows) ? rows : [])
    .filter((j) => j && j.kind === 'layout' && j.status !== 'running' && j.jobId !== keepJobId)
    .map((j) => j.id);
}

/**
 * What a reopened job says about cartons that did not come back (#88), by
 * name. A deleted carton DOES come back (a saved job opens its own; the last
 * run asks for deleted ones), so what is left is a carton its owner has made
 * private since, or one removed for good — and a 404 cannot tell which.
 */
export function missingCartonsMessage(names) {
  const list = (Array.isArray(names) ? names : []).filter(Boolean);
  if (!list.length) return null;
  const one = list.length === 1;
  return `${quoted(list)} ${one ? 'is' : 'are'} no longer available — made private by `
    + `${one ? 'its owner' : 'their owners'}, or removed. The layout shows without ${one ? 'it' : 'them'}.`;
}

/** The cartons a sheet names that have no file on the bench, once each. */
export function missingSourceFiles(sources, filesBySourceId) {
  const files = filesBySourceId || {};
  const out = [];
  for (const s of Array.isArray(sources) ? sources : []) {
    const id = s && s.source_id;
    if (id && !files[id] && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Download PDF, when a carton's file is missing — by name, never by source_id (#88). */
export function missingFilesMessage(ids) {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (!list.length) return null;
  return `This sheet can’t be built: ${quoted(list)} ${list.length === 1 ? 'is' : 'are'} not on the bench — `
    + `${list.length === 1 ? 'it is' : 'they are'} no longer available. Run the layout again without ${list.length === 1 ? 'it' : 'them'}.`;
}

/** The Library's note after a delete (#88): saved jobs that use the carton still open it. */
export function deleteNote(carton) {
  const n = Number(carton && carton.saved_job_count) || 0;
  if (n <= 0) return '';
  return `${n} saved job${n === 1 ? ' uses' : 's use'} it and still open${n === 1 ? 's' : ''} with it.`;
}
